import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Users } from "lucide-react";
import { IMG, METRICS, HERO_OPS } from "../data/content";
import { scrollToId } from "../lib/scroll";

const LINES = [["More Than a"], ["Contractor."], ["A Property", true], ["Operations Partner.", true]];
const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ text, accent, i }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${accent ? "text-[#FF6B00]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.12, ease }}
    >
      {text}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        data-testid="hero-tilt-frame"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.4, delay: 0.5, ease }}
        className="relative aspect-[4/5] w-full rounded-[4px] overflow-hidden bg-[#1C1E24]"
      >
        <img src={IMG.team} alt="The Family — the LHGS team on-site" data-testid="hero-team-photo" className="absolute inset-0 h-full w-full object-cover object-center grayscale-[20%] contrast-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-transparent" />
        <div className="absolute inset-0 spotlight" />
        <div style={{ transform: "translateZ(60px)" }} className="absolute left-4 right-4 bottom-4 sm:left-6 sm:bottom-6 sm:right-auto flex items-center gap-3 rounded-[4px] bg-white text-[#0B0C10] px-4 py-3 shadow-2xl">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#FF6B00] text-white">
            <Users className="h-4 w-4" />
          </span>
          <span className="leading-tight">
            <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-600">LHGS · On-site</span>
            <span className="block font-display font-bold text-sm">The Family</span>
          </span>
        </div>
        <span style={{ transform: "translateZ(40px)" }} className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/90 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full">
          ● REC — Site 019
        </span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#0B0C10] pt-[72px]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img src={IMG.hero} alt="" className="h-full w-full object-cover opacity-[0.18] grayscale" />
      </motion.div>
      <div className="absolute inset-0 tactical-grid opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/40 via-[#0B0C10]/70 to-[#0B0C10]" />
      <div className="grain" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 pt-14 sm:pt-20 lg:pt-24 pb-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-end">
        <motion.div style={{ y: textY }} className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow text-[#FF6B00] mb-7">
            <span className="inline-block w-6 h-px bg-current align-middle mr-3" />
            Ottawa, Ontario · 25+ years combined experience
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display font-black tracking-[-0.03em] leading-[0.95] text-[#F5F5F7] text-[2.6rem] sm:text-6xl lg:text-[4.4rem] xl:text-[5.3rem]">
            {LINES.map(([t, a], i) => (
              <MaskLine key={t} text={t} accent={a} i={i} />
            ))}
          </h1>
          <motion.div
            data-testid="hero-blurb"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease }}
            className="mt-8 max-w-2xl"
          >
            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              <span className="font-semibold text-white">Property Operations: </span>
              {HERO_OPS.map((o, i) => (
                <span key={o}>
                  {o}
                  {i < HERO_OPS.length - 1 ? <span className="text-[#FF6B00]">, </span> : "."}
                </span>
              ))}
            </p>
            <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">
              Servicing Property Managers, Condominium Corporations, Commercial Buildings &amp; Residential Property Owners.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease }} className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("contact")} data-testid="hero-request-service" className="btn-primary btn-lg">
              Request Service <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => scrollToId("services")} data-testid="hero-explore-services" className="btn-outline btn-lg">
              Explore Services
            </button>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-4 max-w-sm sm:max-w-md lg:max-w-none w-full">
          <TiltFrame />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="relative mx-auto max-w-[1440px] px-5 sm:px-8 pb-12"
      >
        <div data-testid="trust-metrics" className="grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
          {METRICS.map((m, i) => (
            <div key={m.label} className={`py-6 pr-4 ${i % 2 ? "pl-4 sm:pl-6 border-l border-white/10" : ""} ${i === 2 ? "lg:pl-6 lg:border-l border-t lg:border-t-0 border-white/10" : ""} ${i === 3 ? "border-t lg:border-t-0" : ""}`}>
              <p className="font-display font-black text-3xl sm:text-4xl text-white">{m.value}</p>
              <p className="mt-1 font-mono text-[11px] sm:text-xs uppercase tracking-[0.14em] text-zinc-400">{m.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
